import { db, auth } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import type { CompanyAnalysis } from '../types/geminiTypes';
import { normalizeTicker } from '../lib/normalizeTicker';
import { BUILD_ID } from '../buildInfo';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  }
console.error('Firestore Error: ', JSON.stringify(errInfo));

  // Fire-and-forget client telemetry
  auth.currentUser?.getIdToken().then(token => {
    fetch('/api/telemetry/client-error', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify({ source: 'verdictLogger', operationType, path, errorMessage: errInfo.error })
    }).catch(() => {});
  }).catch(() => {});

  throw new Error(JSON.stringify(errInfo));
}

/**
 * FEATURE PLANNING: Verdict Log Standard Deviation Analysis
 * 
 * Context: Oracle Insights uses live data and AI, resulting in intrinsic value (IV) 
 * variance between runs for the same ticker/day. E.g., AAPL IV ranged from $185 to $236
 * in a single morning while maintaining a consistent AVOID label.
 * 
 * Future Requirements:
 * - Group entries by ticker and time window (e.g., calendar day)
 * - Compute mean IV across grouped runs
 * - Compute standard deviation of IV across those runs
 * - Flag runs where IV falls outside acceptable standard deviation thresholds
 * - Surface verdict consistency alongside IV variance
 * 
 * Data Architecture Notes:
 * - IV is stored natively as a `number` (`intrinsicValue`), accommodating future cross-run math
 * - Ensure querying supports filtering by ticker and timestamp boundaries for grouping
 */

export async function logVerdict(
  uid: string,
  result: CompanyAnalysis
): Promise<void> {
  try {
    const ticker = result.ticker;
    const verdict = result.oracleFramework?.decision?.value ?? "UNKNOWN";
    const archetype = result.archetype?.category ?? "UNKNOWN";
    const confidence = result.confidenceScore?.score ?? 0;
    const oracleScore = result.oracleScore ?? 0;
    const engineIV = (result as any).scenarios?.base?.intrinsicValuePerShare ?? 0;
    const iv = engineIV > 0 ? engineIV : (result.oracleFramework?.oracleIntrinsicValue ?? 0);
    const price = result.currentPrice ?? 0;

    // [WACC-GAP-PERSIST] (Tuning 10.x) Read the Fix-C axes off the same result.observability the
    // server-side analysis_completed capture uses. Persisting these onto the verdict-log doc lets
    // the backtest verdict-tracker correlate gap-firing vs forward returns — the historical DCF
    // backtest uses a constant WACC and cannot test a per-name floor.
    const obs = (result as any)?.observability ?? {};
    const gap = obs?.waccHurdleGap ?? {};

    // [TRACK-1] Prefer the exact price the engine computed MoS from
    // (runProvenance.priceAtVerdict). Falls back to currentPrice for pre-TRACK-1
    // analyses that carry no provenance. currentPrice is kept as-is below.
    const runProvenance = obs?.runProvenance ?? null;
    const priceAtVerdict =
      typeof runProvenance?.priceAtVerdict === 'number' ? runProvenance.priceAtVerdict : null;
    const mosBasis = priceAtVerdict ?? price;

    let marginOfSafety = 0;
    if (iv > 0) {
      marginOfSafety = Number((((iv - mosBasis) / iv) * 100).toFixed(2));
    }

    const payload = {
      ticker,
      analyzedAt: new Date().toISOString(),
      verdict,
      archetype,
      confidenceScore: confidence,
      oracleScore,
      intrinsicValue: iv,
      currentPrice: price,
      marginOfSafety,
      waccHurdleGapFired: gap?.fired ?? false,
      waccHurdleGapBps: gap?.gapBps ?? null,
      debtToEbitda: gap?.debtToEbitda ?? null,
      waccPctAll: obs?.waccPctAll ?? null,
      beta: obs?.beta ?? null,
      // [MODEL-REGISTRY] Actual modelVersion strings Google returned per agent this
      // run, so the verdict tracker can be sliced by model once the lineup migrates.
      modelVersions: obs?.modelVersions ?? null,
      // [TRACK-1] Run provenance + canonical ticker + the client build that wrote
      // this row. priceAtVerdict is the MoS basis; currentPrice is kept as-is above.
      runProvenance: runProvenance ?? null,
      tickerNormalized: normalizeTicker(ticker),
      clientBuildId: BUILD_ID,
      priceAtVerdict: priceAtVerdict,
      priceAtRecheck: null,
      verdict30d: null,
      verdict90d: null,
    };

    const pathForWrite = `verdictLog/${uid}/analyses`;
    try {
      await addDoc(collection(db, 'verdictLog', uid, 'analyses'), payload);
    } catch (err) {
      handleFirestoreError(err, OperationType.CREATE, pathForWrite);
    }
  } catch (err) {
    console.warn("[VerdictLog] Failed to prep log:", err);
  }
}
